import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runKeywordIntelligence } from "src/common/Scraper";
import { KeywordIntelligenceRunPayload } from "../../types/keyword-assist.type";
import { sleep } from "src/common/config/FunctionHelper";
import useUpdateUserAgents from "../../hooks/useUpdateUserAgents";

import { posthogService, PostHogEvent } from "src/common/services/posthogService";

interface UseKeywordAssistMutationProps {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onMutateStart?: () => void;
  defaultKeywords?: string[];
}

export function useKeywordAssistMutation({
  onSuccess,
  onError,
  onMutateStart,
  defaultKeywords = []
}: UseKeywordAssistMutationProps = {}) {
  const queryClient = useQueryClient();
  const { mutate: updateAgents } = useUpdateUserAgents('keyword-assist');

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [keywords, setKeywords] = useState<string[]>(defaultKeywords);

  const runKeywordIntelligenceMutation = useMutation({
    mutationFn: (payload: KeywordIntelligenceRunPayload) =>
      runKeywordIntelligence(payload),
    onMutate: (variables) => {
      if (variables?.site_id) {
        queryClient.setQueryData(['agentsStatus', variables.site_id], (oldData: any) => ({
          ...oldData,
          agents: {
            ...oldData?.agents,
            'keyword-intelligence': {
              ...oldData?.agents?.['keyword-intelligence'],
              status: 'processing',
            },
          },
        }));
      }
    },
    onSuccess: (data, variables) => {
      // Track Orbit AI Keyword Assist run completion in PostHog
      // Records site ID, user ID, and count of competitor keywords analyzed
      posthogService.track(PostHogEvent.ORBIT_AI_KEYWORD_ASSIST_RUN, {
        site_id: variables?.site_id,
        user_id: variables?.user_id,
        competitors_count: variables?.competitor_keywords?.length || 0
      });

      queryClient.invalidateQueries({
        queryKey: ["keywords-assistance", variables?.user_id, variables?.site_id],
      });
      if (variables?.site_id) {
        queryClient.setQueryData(['agentsStatus', variables.site_id], (oldData: any) => ({
          ...oldData,
          agents: {
            ...oldData?.agents,
            'keyword-intelligence': {
              ...oldData?.agents?.['keyword-intelligence'],
              status: 'processing',
            },
          },
        }));
        queryClient.invalidateQueries({
          queryKey: ["agentsStatus", variables.site_id],
        });
      }
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error, variables) => {
      if (variables?.site_id) {
        queryClient.invalidateQueries({
          queryKey: ["agentsStatus", variables.site_id],
        });
      }
      if (onError) {
        onError(error);
      }
      handleAgentStatusUpdate(false, variables?.site_id, [], 'keyword_assist_dashboard');
    },
  });

  function handleAgentStatusUpdate(status: boolean, overrideSiteId?: string, competitorKeywords?: string[], source: string = 'keyword_assist_dashboard') {
    updateAgents({
      site_id: overrideSiteId,
      source,
      agents: [
        {
          name: 'keyword_assist',
          enabled: status,
          config: {
            competitors: competitorKeywords || []
          }
        }
      ]
    });
  }

  const handleEnableKeywordAssist = async (kws: string[], overrides: { siteId: string; userId: string, shouldRun?: boolean, source?: string }) => {
    const effectiveSiteId = overrides?.siteId;
    const effectiveUserId = overrides?.userId;
    const shouldRun = overrides?.shouldRun ?? true;
    const source = overrides?.source || 'keyword_assist_dashboard';

    if (onMutateStart) {
      onMutateStart();
    }
    setDialogOpen(false);

    handleAgentStatusUpdate(true, overrides?.siteId, kws, source);

    if (shouldRun) {
      runKeywordIntelligenceMutation.mutate({
        stream: false,
        site_id: effectiveSiteId,
        user_id: effectiveUserId,
        competitor_keywords: kws,
      });
    }
  };

  return {
    dialogOpen,
    setDialogOpen,
    keywords,
    setKeywords,
    handleEnableKeywordAssist,
    runKeywordIntelligenceMutation,
  };
}
